// src/screens/GameLibraryScreen.js
// ============================================================================
// GAME LIBRARY SCREEN
//
// Shows the user's owned games with:
// - Install status (downloaded/not downloaded)
// - Play/Download buttons
// - Update notifications
// - Settings for install path
// - Desktop client detection
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  SafeAreaView,
  RefreshControl,
  Alert,
  Platform,
} from 'react-native';
import { httpsCallable } from 'firebase/functions';
import {
  getFirestore, collection, getDocs,
} from 'firebase/firestore';
import { auth, functions } from '../../firebaseConfig';
import { Ionicons } from '@expo/vector-icons';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import {
  isDesktopApp,
  getPlatformInfo,
  getInstalledGames,
  getInstallPath,
  selectInstallPath,
  setInstallPath,
  downloadGame,
  cancelDownload,
  installGame,
  launchGame,
  uninstallGame,
} from '../utils/platform';

export default function GameLibraryScreen({ navigation }) {
  const db = getFirestore();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [libraryGames, setLibraryGames] = useState([]);
  const [localGames, setLocalGames] = useState({});
  const [installDir, setInstallDir] = useState(null);
  const [platformInfo, setPlatformInfo] = useState(null);
  const [downloadStates, setDownloadStates] = useState({}); // gameId -> { progress, status }
  const [showSettings, setShowSettings] = useState(false);
  const { showError } = useError();

  const inDesktopClient = isDesktopApp();

  // Load library on mount
  useEffect(() => {
    loadLibrary();
    
    if (inDesktopClient) {
      loadDesktopInfo();
    }
  }, []);

  const loadDesktopInfo = async () => {
    const [info, installed, path] = await Promise.all([
      getPlatformInfo(),
      getInstalledGames(),
      getInstallPath(),
    ]);
    setPlatformInfo(info);
    setLocalGames(installed || {});
    setInstallDir(path);
  };

  const loadLibrary = async () => {
    if (!auth.currentUser) {
      navigation.navigate('Login');
      return;
    }

    setLoading(true);
    try {
      let games = [];

      // Try cloud function first
      try {
        const getLibrary = httpsCallable(functions, 'getGameLibrary');
        const result = await getLibrary();
        games = result.data.games || [];
      } catch (fnErr) {
        console.log('[GameLibrary] getLibrary unavailable, trying Firestore:', fnErr.message);
      }

      // Firestore fallback - read game_library subcollection directly
      if (games.length === 0 && auth.currentUser) {
        try {
          const libSnap = await getDocs(
            collection(db, 'users', auth.currentUser.uid, 'game_library')
          );
          games = libSnap.docs.map(d => ({ id: d.id, gameId: d.id, ...d.data() }));
          // Sort by lastPlayedAt descending
          games.sort((a, b) => {
            const aTime = a.lastPlayedAt?.toMillis ? a.lastPlayedAt.toMillis() : (a.lastPlayedAt || 0);
            const bTime = b.lastPlayedAt?.toMillis ? b.lastPlayedAt.toMillis() : (b.lastPlayedAt || 0);
            return bTime - aTime;
          });
        } catch (fsErr) {
          console.log('[GameLibrary] Firestore fallback failed:', fsErr.message);
        }
      }

      setLibraryGames(games);
      
      // Also refresh local install status
      if (inDesktopClient) {
        const installed = await getInstalledGames();
        setLocalGames(installed || {});
      }
    } catch (err) {
      console.error('[GameLibrary] Failed to load library:', err);
      showError('Failed to load library');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadLibrary();
  };

  // Handle download
  const handleDownload = async (game) => {
    if (!inDesktopClient) {
      Alert.alert('Desktop Required', 'Download games using the CritCash Desktop App');
      return;
    }

    const platform = platformInfo?.platform || 'win32';
    const downloadUrl = game.downloadUrls?.[platform];

    if (!downloadUrl) {
      showError(`No download available for ${platform}`);
      return;
    }

    // Update download state
    setDownloadStates(prev => ({
      ...prev,
      [game.gameId]: { status: 'downloading', progress: 0 },
    }));

    const result = await downloadGame(game.gameId, downloadUrl, (progress) => {
      setDownloadStates(prev => ({
        ...prev,
        [game.gameId]: { status: 'downloading', progress },
      }));
    });

    if (result.success) {
      // Start installation
      setDownloadStates(prev => ({
        ...prev,
        [game.gameId]: { status: 'installing', progress: 100 },
      }));

      const installResult = await installGame(game.gameId);
      
      if (installResult.success) {
        // Sync install status to backend
        try {
          const updateInstallStatus = httpsCallable(functions, 'updateGameInstallStatus');
          await updateInstallStatus({
            gameId: game.gameId,
            installed: true,
            installedVersion: game.currentVersion,
          });
        } catch (err) {
          console.error('[GameLibrary] Failed to sync install status:', err);
        }
        
        // Refresh
        await loadDesktopInfo();
        await loadLibrary();
      } else {
        showError(installResult.error || 'Installation failed');
      }
      
      // Clear download state
      setDownloadStates(prev => {
        const next = { ...prev };
        delete next[game.gameId];
        return next;
      });
    } else {
      showError(result.error || 'Download failed');
      setDownloadStates(prev => {
        const next = { ...prev };
        delete next[game.gameId];
        return next;
      });
    }
  };

  // Handle cancel download
  const handleCancelDownload = async (gameId) => {
    await cancelDownload(gameId);
    setDownloadStates(prev => {
      const next = { ...prev };
      delete next[gameId];
      return next;
    });
  };

  // Handle play
  const handlePlay = async (gameId) => {
    const result = await launchGame(gameId);
    if (!result.success) {
      showError(result.error || 'Failed to launch game');
    }
  };

  // Handle uninstall
  const handleUninstall = async (game) => {
    const confirmed = Platform.OS === 'web'
      ? window.confirm(`Uninstall ${game.gameName || game.gameId}?`)
      : await new Promise(resolve => {
          Alert.alert(
            'Uninstall Game',
            `Are you sure you want to uninstall ${game.gameName || game.gameId}?`,
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Uninstall', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmed) return;

    const result = await uninstallGame(game.gameId);
    
    if (result.success) {
      // Sync to backend
      try {
        const updateInstallStatus = httpsCallable(functions, 'updateGameInstallStatus');
        await updateInstallStatus({
          gameId: game.gameId,
          installed: false,
        });
      } catch (err) {
        console.error('[GameLibrary] Failed to sync uninstall status:', err);
      }
      
      // Refresh
      await loadDesktopInfo();
      await loadLibrary();
    } else {
      showError(result.error || 'Uninstall failed');
    }
  };

  // Handle change install path
  const handleChangeInstallPath = async () => {
    const newPath = await selectInstallPath();
    if (newPath) {
      await setInstallPath(newPath);
      setInstallDir(newPath);
    }
  };

  // Get game state for rendering
  const getGameState = (game) => {
    const downloadState = downloadStates[game.gameId];
    if (downloadState) return downloadState.status;
    
    const localInfo = localGames[game.gameId];
    if (localInfo?.installed) {
      if (game.needsUpdate) return 'needs_update';
      return 'installed';
    }
    
    return 'not_installed';
  };

  // Render game item
  const renderGameItem = (game) => {
    const state = getGameState(game);
    const downloadState = downloadStates[game.gameId];
    const localInfo = localGames[game.gameId];

    return (
      <View key={game.gameId} style={styles.gameItem}>
        {/* Game Info */}
        <View style={styles.gameInfo}>
          <View style={styles.gameIcon}>
            <Text style={styles.gameIconText}>
              {game.gameName?.[0] || '?'}
            </Text>
          </View>
          <View style={styles.gameDetails}>
            <Text style={styles.gameName}>{game.gameName || game.gameId}</Text>
            <Text style={styles.gameVersion}>
              v{game.currentVersion || '1.0.0'}
              {localInfo?.installedVersion && localInfo.installedVersion !== game.currentVersion && (
                <Text style={styles.updateAvailable}> (Update available)</Text>
              )}
            </Text>
            {state === 'installed' && (
              <Text style={styles.installedBadge}><Ionicons name="checkmark" size={12} color="#2ECC71" /> Installed</Text>
            )}
          </View>
        </View>

        {/* Actions */}
        <View style={styles.gameActions}>
          {state === 'downloading' && (
            <View style={styles.downloadingState}>
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { width: `${downloadState?.progress || 0}%` }
                  ]} 
                />
              </View>
              <Text style={styles.progressText}>
                {Math.round(downloadState?.progress || 0)}%
              </Text>
              <TouchableOpacity 
                style={styles.cancelButton}
                onPress={() => handleCancelDownload(game.gameId)}
              >
                <Ionicons name="close" size={16} color="#e74c3c" />
              </TouchableOpacity>
            </View>
          )}

          {state === 'installing' && (
            <View style={styles.installingState}>
              <ActivityIndicator size="small" color="#FCD000" />
              <Text style={styles.installingText}>Installing...</Text>
            </View>
          )}

          {state === 'installed' && (
            <View style={styles.installedActions}>
              <TouchableOpacity
                style={styles.playButton}
                onPress={() => handlePlay(game.gameId)}
              >
                <Text style={styles.playButtonText}><Ionicons name="play" size={12} color="#000" /> Play</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.moreButton}
                onPress={() => handleUninstall(game)}
              >
                <Ionicons name="ellipsis-horizontal" size={16} color="#ccc" />
              </TouchableOpacity>
            </View>
          )}

          {state === 'needs_update' && (
            <TouchableOpacity
              style={styles.updateButton}
              onPress={() => handleDownload(game)}
            >
              <Text style={styles.updateButtonText}><Ionicons name="refresh" size={12} color="#FCD000" /> Update</Text>
            </TouchableOpacity>
          )}

          {state === 'not_installed' && (
            <TouchableOpacity
              style={styles.downloadButton}
              onPress={() => handleDownload(game)}
            >
              <Text style={styles.downloadButtonText}>
                {inDesktopClient ? 'Download' : 'Get App'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FCD000" />
          <Text style={styles.loadingText}>Loading library...</Text>
        </View>
        <Footer navigation={navigation} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#FCD000"
            colors={['#FCD000']}
          />
        }
      >
        {/* Page Header */}
        <View style={styles.pageHeader}>
          <Text style={styles.pageTitle}><Ionicons name="library-outline" size={20} color="#FCD000" /> My Library</Text>
          {inDesktopClient && (
            <TouchableOpacity
              style={styles.settingsButton}
              onPress={() => setShowSettings(!showSettings)}
            >
              <Ionicons name="settings-outline" size={20} color="#ccc" />
            </TouchableOpacity>
          )}
        </View>

        {/* Desktop Status Banner */}
        {inDesktopClient ? (
          <View style={styles.statusBanner}>
            <Ionicons name="checkmark-circle" size={16} color="#2ECC71" />
            <View style={styles.statusContent}>
              <Text style={styles.statusTitle}>Desktop Client Connected</Text>
              <Text style={styles.statusSubtitle}>
                v{platformInfo?.version || '1.0.0'} · Ready to download games
              </Text>
            </View>
          </View>
        ) : (
          <TouchableOpacity 
            style={styles.getAppBanner}
            onPress={() => navigation.navigate('Download')}
          >
            <Ionicons name="desktop-outline" size={24} color="#FCD000" />
            <View style={styles.getAppContent}>
              <Text style={styles.getAppTitle}>Get the Desktop App</Text>
              <Text style={styles.getAppSubtitle}>Download and install games on your PC</Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color="#666" />
          </TouchableOpacity>
        )}

        {/* Settings Panel */}
        {showSettings && inDesktopClient && (
          <View style={styles.settingsPanel}>
            <Text style={styles.settingsTitle}>Settings</Text>
            <View style={styles.settingRow}>
              <Text style={styles.settingLabel}>Install Location</Text>
              <TouchableOpacity 
                style={styles.settingValue}
                onPress={handleChangeInstallPath}
              >
                <Text style={styles.settingValueText} numberOfLines={1}>
                  {installDir || 'Default'}
                </Text>
                <Text style={styles.settingChange}>Change</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* Games List */}
        {libraryGames.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="mail-open-outline" size={48} color="#666" />
            <Text style={styles.emptyTitle}>Your library is empty</Text>
            <Text style={styles.emptyText}>
              Browse the store to find games to add
            </Text>
            <TouchableOpacity
              style={styles.browseButton}
              onPress={() => navigation.navigate('Store')}
            >
              <Text style={styles.browseButtonText}>Browse Store</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.gamesList}>
            {/* Installed Games */}
            {libraryGames.filter(g => getGameState(g) === 'installed' || getGameState(g) === 'needs_update').length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Ready to Play</Text>
                {libraryGames
                  .filter(g => getGameState(g) === 'installed' || getGameState(g) === 'needs_update')
                  .map(renderGameItem)}
              </View>
            )}

            {/* In Progress */}
            {libraryGames.filter(g => ['downloading', 'installing'].includes(getGameState(g))).length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>In Progress</Text>
                {libraryGames
                  .filter(g => ['downloading', 'installing'].includes(getGameState(g)))
                  .map(renderGameItem)}
              </View>
            )}

            {/* Not Installed */}
            {libraryGames.filter(g => getGameState(g) === 'not_installed').length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Not Installed</Text>
                {libraryGames
                  .filter(g => getGameState(g) === 'not_installed')
                  .map(renderGameItem)}
              </View>
            )}
          </View>
        )}
      </ScrollView>

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0b0b0b',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 12,
    paddingBottom: 120,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },

  // Page Header
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#FCD000',
  },
  settingsButton: {
    padding: 8,
  },
  settingsButtonText: {
    fontSize: 20,
  },

  // Status Banners
  statusBanner: {
    backgroundColor: '#1a3a1a',
    borderRadius: 12,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#22c55e',
  },
  statusIcon: {
    fontSize: 18,
    color: '#22c55e',
    marginRight: 12,
  },
  statusContent: {
    flex: 1,
  },
  statusTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#22c55e',
  },
  statusSubtitle: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },

  getAppBanner: {
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#2563eb',
  },
  getAppIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  getAppContent: {
    flex: 1,
  },
  getAppTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  getAppSubtitle: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  getAppArrow: {
    fontSize: 18,
    color: '#2563eb',
  },

  // Settings Panel
  settingsPanel: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#222',
  },
  settingsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 16,
  },
  settingRow: {
    marginBottom: 12,
  },
  settingLabel: {
    fontSize: 13,
    color: '#888',
    marginBottom: 6,
  },
  settingValue: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#333',
  },
  settingValueText: {
    flex: 1,
    fontSize: 13,
    color: '#ccc',
  },
  settingChange: {
    fontSize: 13,
    color: '#FCD000',
    marginLeft: 8,
  },

  // Sections
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '800',
    color: '#888',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },

  // Game Item
  gamesList: {},
  gameItem: {
    backgroundColor: '#0f0f0f',
    borderRadius: 12,
    padding: 14,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#1a1a1a',
  },
  gameInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  gameIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#1a1a1a',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  gameIconText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#666',
  },
  gameDetails: {
    flex: 1,
  },
  gameName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 2,
  },
  gameVersion: {
    fontSize: 12,
    color: '#666',
  },
  updateAvailable: {
    color: '#f59e0b',
  },
  installedBadge: {
    fontSize: 11,
    color: '#22c55e',
    marginTop: 4,
  },

  // Actions
  gameActions: {
    marginLeft: 12,
  },
  downloadingState: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  progressBar: {
    width: 80,
    height: 6,
    backgroundColor: '#222',
    borderRadius: 3,
    overflow: 'hidden',
    marginRight: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#2563eb',
  },
  progressText: {
    fontSize: 11,
    color: '#888',
    marginRight: 8,
    width: 32,
  },
  cancelButton: {
    padding: 4,
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#e57373',
  },

  installingState: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  installingText: {
    fontSize: 13,
    color: '#888',
    marginLeft: 8,
  },

  installedActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  playButton: {
    backgroundColor: '#22c55e',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    marginRight: 8,
  },
  playButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#000',
  },
  moreButton: {
    padding: 8,
  },
  moreButtonText: {
    fontSize: 18,
    color: '#666',
  },

  updateButton: {
    backgroundColor: '#f59e0b',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  updateButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#000',
  },

  downloadButton: {
    backgroundColor: '#2563eb',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  downloadButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#fff',
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    padding: 60,
  },
  emptyEmoji: {
    fontSize: 64,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
    marginBottom: 24,
  },
  browseButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  browseButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
  },
});